import { useCallback, useEffect, useMemo, useState } from 'react';

import { MAX_RELATED_INBOX_ENTRIES } from '@this+that/constants';
import { InboxEntry } from '@this+that/models';
import log from 'loglevel';

import { useUpdateInboxEntryCollections } from './useUpdateInboxEntryCollections';
import { useInboxEntriesStore } from '../../store/Inbox/InboxEntries';

export const LINKED_INBOX_ENTRIES_LINK_ERROR_KEY =
  'components.inboxEntryDetailsModal.linkedTasks.linkError';

export interface UseLinkedInboxEntriesParams {
  inboxEntry: InboxEntry;
  refetchDetails: () => Promise<unknown> | void;
}

export interface UseLinkedInboxEntriesResult {
  linkedEntries: InboxEntry[];
  isHydrated: boolean;
  resolvedCount: number;
  totalCount: number;
  remainingAllowance: number;
  isAtCeiling: boolean;
  isMutating: boolean;
  link: (targetIds: string[]) => Promise<void>;
  unlink: (targetId: string) => Promise<void>;
}

export const useLinkedInboxEntries = ({
  inboxEntry,
  refetchDetails,
}: UseLinkedInboxEntriesParams): UseLinkedInboxEntriesResult => {
  const { handleUpdateInboxEntryCollections, loading } =
    useUpdateInboxEntryCollections();
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const storeInboxEntries = useInboxEntriesStore(
    (state) => state.taskManager.activeTaskGroup.inboxEntries,
  );

  const hydratedEntries = inboxEntry.relatedInboxEntries;
  const isHydrated = hydratedEntries !== undefined;

  // Once an unlinked id is no longer present in the hydrated data (the removal has landed
  // server-side), drop it from the optimistic set so a future re-link of the same entry isn't
  // silently filtered out.
  useEffect(() => {
    setRemovedIds((prev) => {
      if (prev.size === 0) return prev;
      const stillPresentIds = new Set(
        (inboxEntry.relatedInboxEntries ?? []).map((entry) => entry.id),
      );
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (stillPresentIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [inboxEntry.relatedInboxEntries]);

  // Row membership comes from relatedInboxEntries (the server already drops ids whose
  // target was deleted or moved out of the task group); row content is resolved against
  // the live store so edits made elsewhere show up without a refetch.
  const linkedEntries = useMemo(() => {
    const liveById = new Map(
      storeInboxEntries.map((entry) => [entry.id, entry]),
    );
    return (hydratedEntries ?? [])
      .filter((entry) => !removedIds.has(entry.id))
      .map((entry) => liveById.get(entry.id) ?? entry);
  }, [hydratedEntries, removedIds, storeInboxEntries]);

  const totalCount = linkedEntries.length;
  const resolvedCount = linkedEntries.filter(
    (entry) => entry.resolvedAt != null,
  ).length;
  const remainingAllowance = Math.max(
    0,
    MAX_RELATED_INBOX_ENTRIES - totalCount,
  );
  const isAtCeiling = remainingAllowance === 0;

  const link = useCallback(
    async (targetIds: string[]) => {
      try {
        await handleUpdateInboxEntryCollections(inboxEntry.id, {
          relatedInboxEntryIds: { add: targetIds },
        });
        setRemovedIds((prev) => {
          const next = new Set(prev);
          targetIds.forEach((id) => next.delete(id));
          return next;
        });
        await refetchDetails();
      } catch (error) {
        log.error('Failed to link inbox entries:', error);
        throw new Error(LINKED_INBOX_ENTRIES_LINK_ERROR_KEY);
      }
    },
    [inboxEntry.id, handleUpdateInboxEntryCollections, refetchDetails],
  );

  const unlink = useCallback(
    async (targetId: string) => {
      setRemovedIds((prev) => new Set(prev).add(targetId));
      try {
        await handleUpdateInboxEntryCollections(inboxEntry.id, {
          relatedInboxEntryIds: { remove: [targetId] },
        });
      } catch (error) {
        log.error('Failed to unlink inbox entry:', error);
        setRemovedIds((prev) => {
          const next = new Set(prev);
          next.delete(targetId);
          return next;
        });
        throw new Error(LINKED_INBOX_ENTRIES_LINK_ERROR_KEY);
      }
    },
    [inboxEntry.id, handleUpdateInboxEntryCollections],
  );

  return {
    linkedEntries,
    isHydrated,
    resolvedCount,
    totalCount,
    remainingAllowance,
    isAtCeiling,
    isMutating: loading,
    link,
    unlink,
  };
};
